import { Link, useParams } from "react-router-dom";

import { adminAgenciesApi } from "@/api/admin";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { FullPageSpinner } from "@/components/FullPageSpinner";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useRecordForm } from "@/hooks/useRecordForm";

export const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "India Standard Time (IST - Asia/Kolkata)" },
  { value: "America/New_York", label: "Eastern Time (EST/EDT - America/New_York)" },
  { value: "America/Chicago", label: "Central Time (CST/CDT - America/Chicago)" },
  { value: "America/Denver", label: "Mountain Time (MST/MDT - America/Denver)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PST/PDT - America/Los_Angeles)" },
  { value: "America/Anchorage", label: "Alaska Time (AKST/AKDT - America/Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST - Pacific/Honolulu)" },
  { value: "Europe/London", label: "UK / London (GMT/BST - Europe/London)" },
  { value: "Europe/Paris", label: "Central Europe / Paris (CET/CEST - Europe/Paris)" },
  { value: "Europe/Berlin", label: "Central Europe / Berlin (CET/CEST - Europe/Berlin)" },
  { value: "Asia/Dubai", label: "Gulf Standard Time (GST - Asia/Dubai)" },
  { value: "Asia/Singapore", label: "Singapore Time (SGT - Asia/Singapore)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST - Asia/Tokyo)" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (AEST/AEDT - Australia/Sydney)" },
  { value: "UTC", label: "Coordinated Universal Time (UTC)" },
];

const BLANK_AGENCY = {
  name: "",
  timezone: "America/New_York",
  contact_email: "",
  contact_phone: "",
  is_active: true,
};

const STATUS_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export function AgencyFormPage() {
  const { id } = useParams();
  const {
    isNew,
    values,
    setValue,
    handleChange,
    isLoading,
    isSaving,
    isDirty,
    discard,
    error,
    fieldErrors,
    submit,
    remove,
  } = useRecordForm({
    resource: adminAgenciesApi,
    id,
    blankValues: BLANK_AGENCY,
    redirectTo: "/admin/agencies",
  });

  const saveDisabled = isSaving || (!isNew && !isDirty);

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to permanently delete this organization?")) {
      remove();
    }
  };

  const handleFormSubmit = (event) => {
    if (!isNew && !isDirty) {
      event.preventDefault();
      return;
    }
    submit(event);
  };

  if (isLoading) {
    return <FullPageSpinner label="Loading organization details" />;
  }

  const timezoneOptions =
    values.timezone && !TIMEZONE_OPTIONS.some((o) => o.value === values.timezone)
      ? [...TIMEZONE_OPTIONS, { value: values.timezone, label: values.timezone }]
      : TIMEZONE_OPTIONS;

  return (
    <PageShell>
      <PageHeader
        title={isNew ? "New organization" : values.name}
        description={
          isNew
            ? "Create a new tenant organization on the platform."
            : "Edit tenant organization details."
        }
      />

      <PageBody>
        <Alert>{error}</Alert>

        <form onSubmit={handleFormSubmit} noValidate>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organization details</CardTitle>
              <CardDescription>
                Name, timezone, and contact information for this tenant.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Organization name" name="name" error={fieldErrors.name} className="sm:col-span-2">
                  {(props) => (
                    <Input
                      {...props}
                      type="text"
                      required
                      value={values.name}
                      onChange={handleChange}
                      placeholder="e.g. Riverside Home Care"
                    />
                  )}
                </Field>

                <Field
                  label="Organization Timezone"
                  name="timezone"
                  error={fieldErrors.timezone}
                  hint="Governs shift scheduling, today's visit resolution, and care plan calculations."
                  className="sm:col-span-2"
                >
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={values.timezone}
                      onValueChange={(v) =>
                        handleChange({ target: { name: props.name, value: v } })
                      }
                      options={timezoneOptions}
                      placeholder="Select timezone…"
                    />
                  )}
                </Field>

                <Field label="Contact email" name="contact_email" error={fieldErrors.contact_email}>
                  {(props) => (
                    <Input
                      {...props}
                      type="email"
                      value={values.contact_email}
                      onChange={handleChange}
                      placeholder="e.g. contact@agency.com"
                    />
                  )}
                </Field>

                <Field label="Contact phone" name="contact_phone" error={fieldErrors.contact_phone}>
                  {(props) => (
                    <Input
                      {...props}
                      type="tel"
                      value={values.contact_phone}
                      onChange={handleChange}
                      placeholder="e.g. +1 555 0199"
                    />
                  )}
                </Field>

                <Field label="Status" name="is_active" error={fieldErrors.is_active}>
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={values.is_active.toString()}
                      onValueChange={(v) => setValue("is_active", v === "true")}
                      options={STATUS_OPTIONS}
                      placeholder="Select status…"
                    />
                  )}
                </Field>
              </div>
            </CardContent>
            <CardFooter className="flex flex-wrap items-center gap-2 border-t border-border px-4 pt-4">
              <Button type="submit" disabled={saveDisabled}>
                {isSaving ? "Saving…" : "Save organization"}
              </Button>
              {!isNew ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!isDirty || isSaving}
                  onClick={discard}
                >
                  Discard
                </Button>
              ) : null}
              <Button type="button" variant="ghost" asChild>
                <Link to="/admin/agencies">Cancel</Link>
              </Button>
              {!isNew ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="ml-auto"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete organization
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        </form>
      </PageBody>
    </PageShell>
  );
}
